import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomerPicker } from '@/components/customer-picker';
import { RepairStatusBadge } from '@/components/repairs';
import { Button } from '@/components/ui/button';
import { MonitoringCard } from '@/components/monitoring';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Card, CardContent, CardHeader, CardTitle, Input, Skeleton, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { DeviceDetail, DeviceItem, Paginated, Ref } from '@/lib/types';
import { formatDate } from '@/lib/utils';

type Form = { manufacturer: string; deviceType: string; model: string; serialNumber: string; inventoryNumber: string; purchaseDate: string; warrantyUntil: string; notes: string; customer: Ref | null };

function DeviceDialog({ device, open, onOpenChange, onSaved }: { device: DeviceDetail | null; open: boolean; onOpenChange: (o: boolean) => void; onSaved: (d: DeviceDetail) => void }) {
  const [f, setF] = useState<Form>({ manufacturer: '', deviceType: '', model: '', serialNumber: '', inventoryNumber: '', purchaseDate: '', warrantyUntil: '', notes: '', customer: null });
  useEffect(() => {
    if (!open) return;
    setF({
      manufacturer: device?.manufacturer ?? '',
      deviceType: device?.deviceType ?? '',
      model: device?.model ?? '',
      serialNumber: device?.serialNumber ?? '',
      inventoryNumber: device?.inventoryNumber ?? '',
      purchaseDate: device?.purchaseDate?.slice(0, 10) ?? '',
      warrantyUntil: device?.warrantyUntil?.slice(0, 10) ?? '',
      notes: device?.notes ?? '',
      customer: device?.customer ?? null,
    });
  }, [open, device]);
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        manufacturer: f.manufacturer,
        deviceType: f.deviceType,
        model: f.model,
        serialNumber: f.serialNumber || null,
        inventoryNumber: f.inventoryNumber || null,
        purchaseDate: f.purchaseDate || null,
        warrantyUntil: f.warrantyUntil || null,
        notes: f.notes || null,
        customerId: f.customer?.id ?? null,
      };
      return device ? api<DeviceDetail>(`/devices/${device.id}`, { method: 'PUT', body }) : api<DeviceDetail>('/devices', { method: 'POST', body });
    },
    onSuccess: (d) => {
      onSaved(d);
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const valid = f.manufacturer.trim() && f.deviceType.trim() && f.model.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>{device ? 'Gerät bearbeiten' : 'Neues Gerät'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="grid gap-4 sm:grid-cols-2">
          <Field label="Gerätetyp" htmlFor="d-type">
            <Input id="d-type" value={f.deviceType} onChange={set('deviceType')} />
          </Field>
          <Field label="Hersteller" htmlFor="d-man">
            <Input id="d-man" value={f.manufacturer} onChange={set('manufacturer')} />
          </Field>
          <Field label="Modell" htmlFor="d-model">
            <Input id="d-model" value={f.model} onChange={set('model')} />
          </Field>
          <Field label="Seriennummer" htmlFor="d-sn">
            <Input id="d-sn" className="font-mono" value={f.serialNumber} onChange={set('serialNumber')} />
          </Field>
          <Field label="Inventarnummer" htmlFor="d-inv">
            <Input id="d-inv" value={f.inventoryNumber} onChange={set('inventoryNumber')} />
          </Field>
          <Field label="Kunde" htmlFor="d-cust">
            <CustomerPicker id="d-cust" value={f.customer} onChange={(c) => setF((x) => ({ ...x, customer: c }))} />
          </Field>
          <Field label="Kaufdatum" htmlFor="d-buy">
            <Input id="d-buy" type="date" value={f.purchaseDate} onChange={set('purchaseDate')} />
          </Field>
          <Field label="Garantie bis" htmlFor="d-war">
            <Input id="d-war" type="date" value={f.warrantyUntil} onChange={set('warrantyUntil')} />
          </Field>
          <Field label="Notizen" htmlFor="d-notes" className="sm:col-span-2">
            <Textarea id="d-notes" rows={3} value={f.notes} onChange={set('notes')} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid} loading={save.isPending}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DevicesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (q) params.set('q', q);
  const { data, isLoading } = useQuery({ queryKey: ['devices', 'list', params.toString()], queryFn: () => api<Paginated<DeviceItem>>(`/devices?${params}`), placeholderData: keepPreviousData });

  return (
    <>
      <PageHeader
        title="Geräte"
        description="Kundengeräte mit Reparaturhistorie"
        actions={
          can('devices.manage') && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> Gerät
            </Button>
          )
        }
      />
      <Card>
        <div className="border-b p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" placeholder="Seriennummer, Inventarnummer, Modell, Hersteller, Kunde" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Suche" />
          </div>
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Gerät</TH>
              <TH className="hidden md:table-cell">Seriennummer</TH>
              <TH>Kunde</TH>
              <TH className="text-right">Reparaturen</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={4}>
                  <Skeleton className="h-24" />
                </TD>
              </TR>
            )}
            {data?.items.length === 0 && <EmptyRow colSpan={4}>Keine Geräte gefunden.</EmptyRow>}
            {data?.items.map((d) => (
              <TR key={d.id} className="cursor-pointer" onClick={() => navigate(`/devices/${d.id}`)}>
                <TD>
                  <p className="font-medium">
                    {d.manufacturer} {d.model}
                  </p>
                  <p className="text-xs text-muted-foreground">{d.deviceType}</p>
                </TD>
                <TD className="hidden font-mono text-xs md:table-cell">{d.serialNumber ?? '–'}</TD>
                <TD>{d.customer?.name ?? <span className="text-muted-foreground">–</span>}</TD>
                <TD className="text-right tabular-nums">{d.repairCount}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
      <DeviceDialog
        device={null}
        open={open}
        onOpenChange={setOpen}
        onSaved={(d) => {
          qc.invalidateQueries({ queryKey: ['devices'] });
          navigate(`/devices/${d.id}`);
        }}
      />
    </>
  );
}

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: d } = useQuery({ queryKey: ['devices', id], queryFn: () => api<DeviceDetail>(`/devices/${id}`) });
  if (!d) return <Skeleton className="h-64" />;
  return (
    <>
      <Link to="/devices" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Geräte
      </Link>
      <PageHeader
        title={`${d.manufacturer} ${d.model}`}
        description={d.deviceType}
        actions={
          <>
            {can('repairs.intake') && d.customer && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/repairs/new?customerId=${d.customer.id}&customerName=${encodeURIComponent(d.customer.name)}`}>
                  <Plus /> Reparatur
                </Link>
              </Button>
            )}
            {can('devices.manage') && (
              <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
                <Pencil /> Bearbeiten
              </Button>
            )}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Card className="h-fit">
          <CardContent className="grid gap-2 pt-5 text-sm">
            <p>
              <span className="text-muted-foreground">Seriennummer:</span> <span className="font-mono">{d.serialNumber ?? '–'}</span>
            </p>
            {d.inventoryNumber && (
              <p>
                <span className="text-muted-foreground">Inventarnummer:</span> {d.inventoryNumber}
              </p>
            )}
            <p>
              <span className="text-muted-foreground">Kunde:</span>{' '}
              {d.customer ? (
                <Link to={`/customers/${d.customer.id}`} className="hover:underline">
                  {d.customer.name}
                </Link>
              ) : (
                '–'
              )}
            </p>
            {d.purchaseDate && (
              <p>
                <span className="text-muted-foreground">Kaufdatum:</span> {formatDate(d.purchaseDate)}
              </p>
            )}
            {d.warrantyUntil && (
              <p>
                <span className="text-muted-foreground">Garantie bis:</span> {formatDate(d.warrantyUntil)}
                {new Date(d.warrantyUntil) < new Date() && <span className="text-muted-foreground"> (abgelaufen)</span>}
              </p>
            )}
            {d.notes && <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-xs">{d.notes}</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Reparaturhistorie</CardTitle>
          </CardHeader>
          <Table>
            <TBody>
              {d.repairs.length === 0 && <EmptyRow colSpan={3}>Noch keine Reparaturen.</EmptyRow>}
              {d.repairs.map((r) => (
                <TR key={r.id} className="cursor-pointer" onClick={() => navigate(`/repairs/${r.id}`)}>
                  <TD className="font-mono text-xs">{r.number}</TD>
                  <TD className="max-w-sm truncate">{r.faultDescription}</TD>
                  <TD className="text-right">
                    <RepairStatusBadge status={r.status} />
                    <p className="mt-1 text-xs text-muted-foreground">{formatDate(r.receivedAt)}</p>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
        <div className="lg:col-start-2">
          <MonitoringCard deviceId={d.id} />
        </div>
      </div>
      <DeviceDialog device={d} open={open} onOpenChange={setOpen} onSaved={(x) => qc.setQueryData(['devices', id], x)} />
    </>
  );
}
